import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Select, Modal, DatePicker, Radio, message } from 'antd';
import { HistoryOutlined, SendOutlined } from '@ant-design/icons';
import locale from 'antd/es/date-picker/locale/pt_BR';
import moment from 'moment';

import api from '../../../api';
import formatPriceForInput from '../../../helpers/formatPrice';
import './edit-interested-customer.css';

const weekDays = [
    { value: 'Segunda-feira', label: 'Segunda' },
    { value: 'Terça-feira', label: 'Terça' },
    { value: 'Quarta-feira', label: 'Quarta' },
    { value: 'Quinta-feira', label: 'Quinta' },
    { value: 'Sexta-feira', label: 'Sexta' },
    { value: 'Sábado', label: 'Sábado' },
    { value: 'Domingo', label: 'Domingo' }
];

// Mapeamento do índice do dia da semana (0=Domingo) para o valor
const weekDayMap = {
    0: 'Domingo',
    1: 'Segunda-feira',
    2: 'Terça-feira',
    3: 'Quarta-feira',
    4: 'Quinta-feira',
    5: 'Sexta-feira',
    6: 'Sábado'
};

function EditInterestedCustomer({
    users,
    interestedCustomer,
    setInterestedCustomer,
    setInterestedCustomers,
    modalInterestedCustomer,
    setModalInterestedCustomer,
    loadData,
    userData,
    isAdmin
}) {
    const [form] = Form.useForm();
    const [statusForm] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [addingStatus, setAddingStatus] = useState(false);
    const [price, setPrice] = useState('');

    function changePrice(value) {
        setPrice(formatPriceForInput(value));
    }

    function handleDateChange(date) {
        if (date) {
            const dayOfWeek = date.day();
            form.setFieldsValue({ weekDay: weekDayMap[dayOfWeek] });
        }
    }

    useEffect(() => {
        if (interestedCustomer && interestedCustomer._id) {
            form.setFieldsValue({
                seller: interestedCustomer.seller?._id,
                company: interestedCustomer.company,
                weekDay: interestedCustomer.weekDay,
                interestDate: interestedCustomer.interestDate ? moment(interestedCustomer.interestDate) : null,
                priority: interestedCustomer.priority,
                hasProposal: interestedCustomer.hasProposal,
                originListing: interestedCustomer.originListing,
                sellTo: interestedCustomer.sellTo
            });
            setPrice(interestedCustomer.value || '');
        }
    }, [interestedCustomer, form]);

    function closeModal() {
        setModalInterestedCustomer(false);
        setInterestedCustomer({});
        form.resetFields();
        statusForm.resetFields();
        setPrice('');
    }

    async function onFinish(values) {
        setLoading(true);

        try {
            const data = {
                seller: values.seller,
                company: values.company,
                weekDay: values.weekDay,
                interestDate: values.interestDate ? values.interestDate.toISOString() : null,
                priority: values.priority,
                hasProposal: values.hasProposal,
                originListing: values.originListing,
                sellTo: values.sellTo,
                value: price || ''
            };

            await api.put(`/interested-customers/${interestedCustomer._id}`, data);
            
            message.success('Cliente interessado atualizado com sucesso!');
            loadData();
            closeModal();
        } catch (error) {
            console.error('Erro ao atualizar:', error);
            message.error('Erro ao atualizar cliente interessado');
        }

        setLoading(false);
    }

    async function addStatus(values) {
        if (!values.newStatus || values.newStatus.trim() === '') {
            message.warning('Digite uma observação');
            return;
        }

        setAddingStatus(true);

        try {
            const data = {
                message: values.newStatus,
                userId: userData._id
            };

            const response = await api.post(`/interested-customers/${interestedCustomer._id}/status`, data);
            
            // Atualiza o histórico local
            setInterestedCustomer(response.data);
            
            message.success('Status adicionado com sucesso!');
            statusForm.resetFields();
            loadData();
        } catch (error) {
            console.error('Erro ao adicionar status:', error);
            message.error('Erro ao adicionar status');
        }

        setAddingStatus(false);
    }

    function formatDate(date) {
        return moment(date).format('DD/MM/YYYY [às] HH:mm');
    }

    if (!interestedCustomer || !interestedCustomer._id) {
        return null;
    }

    return (
        <Modal
            title="Editar Cliente Interessado"
            visible={modalInterestedCustomer}
            onCancel={closeModal}
            footer={
                <div className="edit-interested-customer-modal-footer">
                    <Button onClick={closeModal}>Cancelar</Button>
                    <Button type="primary" loading={loading} onClick={() => form.submit()}>
                        Salvar Alterações
                    </Button>
                </div>
            }
            width={1000}
            centered
            maskClosable={false}
            className="edit-interested-customer-modal"
        >
            <div className="edit-interested-customer-layout">
                {/* Coluna esquerda: formulário */}
                <div className="edit-interested-customer-form edit-interested-customer-form-col">
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                    >
                        <div className="form-row">
                            {isAdmin ? (
                                <Form.Item
                                    name="seller"
                                    label="Vendedor"
                                    rules={[{ required: true, message: 'Vendedor é obrigatório' }]}
                                >
                                    <Select
                                        placeholder="Selecione o vendedor"
                                        showSearch
                                        optionFilterProp="children"
                                    >
                                        {users.map(user => (
                                            <Select.Option key={user._id} value={user._id}>
                                                {user.name}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            ) : (
                                <>
                                    <Form.Item name="seller" hidden>
                                        <Input />
                                    </Form.Item>
                                    <Form.Item label="Vendedor">
                                        <Input value={interestedCustomer.seller?.name} disabled />
                                    </Form.Item>
                                </>
                            )}

                            <Form.Item
                                name="company"
                                label="Empresa"
                                rules={[{ required: true, message: 'Empresa é obrigatória' }]}
                            >
                                <Input placeholder="Nome da empresa" />
                            </Form.Item>
                        </div>

                        <div className="form-row">
                            <Form.Item
                                name="weekDay"
                                label="Dia da Semana"
                            >
                                <Select placeholder="Selecione" allowClear>
                                    {weekDays.map(day => (
                                        <Select.Option key={day.value} value={day.value}>
                                            {day.label}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            <Form.Item
                                name="interestDate"
                                label="Data do Interesse"
                                rules={[{ required: true, message: 'Data é obrigatória' }]}
                            >
                                <DatePicker
                                    format="DD/MM/YYYY"
                                    placeholder="Selecione a data"
                                    locale={locale}
                                    style={{ width: '100%' }}
                                    onChange={handleDateChange}
                                />
                            </Form.Item>
                        </div>

                        <div className="form-row">
                            <Form.Item
                                name="priority"
                                label="Prioridade"
                            >
                                <Radio.Group className="priority-selector">
                                    <Radio.Button value="high" className="high">Alta</Radio.Button>
                                    <Radio.Button value="medium" className="medium">Média</Radio.Button>
                                    <Radio.Button value="low" className="low">Baixa</Radio.Button>
                                </Radio.Group>
                            </Form.Item>

                            <Form.Item
                                name="hasProposal"
                                label="Proposta Enviada"
                            >
                                <Radio.Group className="proposal-selector">
                                    <Radio.Button value={true}>Sim</Radio.Button>
                                    <Radio.Button value={false}>Não</Radio.Button>
                                </Radio.Group>
                            </Form.Item>
                        </div>

                        <div className="form-row">
                            <Form.Item
                                name="originListing"
                                label="Origem da Listagem"
                            >
                                <Input placeholder="Ex: Amil, SulAmérica, Notredame..." />
                            </Form.Item>

                            <Form.Item
                                name="sellTo"
                                label="Vender"
                            >
                                <Input placeholder="Ex: Porto Seguro, Bradesco..." />
                            </Form.Item>
                        </div>

                        <Form.Item label="Valor">
                            <Input
                                value={price}
                                onChange={(e) => changePrice(e.target.value)}
                                placeholder="R$ 0,00"
                                style={{ width: '50%' }}
                            />
                        </Form.Item>
                    </Form>
                </div>

                {/* Coluna direita: timeline de status */}
                <div className="status-history-section">
                    <h4>
                        <HistoryOutlined /> Histórico de Status
                    </h4>

                    <div className={`status-history-timeline ${interestedCustomer.statusHistory?.length ? 'has-items' : ''}`}>
                        {interestedCustomer.statusHistory && interestedCustomer.statusHistory.length > 0 ? (
                            [...interestedCustomer.statusHistory].reverse().map((status, index) => (
                                <div key={index} className="timeline-item">
                                    <div className="timeline-dot" />
                                    <div className="timeline-bubble">
                                        <div className="bubble-message">{status.message}</div>
                                        <div className="bubble-meta">
                                            <span className="bubble-user">{status.user?.name || 'Usuário'}</span>
                                            <span className="bubble-date">{formatDate(status.createdAt)}</span>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="empty-history">
                                Nenhum status registrado ainda
                            </div>
                        )}
                    </div>

                    <div className="add-status-form">
                        <h5>Adicionar nova observação</h5>
                        <Form
                            form={statusForm}
                            layout="vertical"
                            onFinish={addStatus}
                        >
                            <Form.Item name="newStatus" style={{ marginBottom: 10 }}>
                                <Input.TextArea
                                    rows={2}
                                    placeholder="Digite uma nova observação..."
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                            <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SendOutlined />}
                                loading={addingStatus}
                                block
                            >
                                Adicionar
                            </Button>
                        </Form>
                    </div>
                </div>
            </div>
        </Modal>
    );
}

export default EditInterestedCustomer;
